import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import SensorsIcon from '@mui/icons-material/Sensors';
import VideocamIcon from '@mui/icons-material/Videocam';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';

import { getCameraList, sendCameraHeartbeat } from '../../services/cameraService';
import { getFallEvents } from '../../services/fallEventService';
import { resolveStatusToken } from '../../constants/statusTokens';
import config from '../../config/app.config';

import ActiveCameraGrid from './components/monitoring/ActiveCameraGrid';
import AttentionCameraList from './components/monitoring/AttentionCameraList';
import LivePreviewDialog from './components/monitoring/LivePreviewDialog';

const normalizeEvents = (data) => {
  if (!data) return [];
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
};

const STALE_HEARTBEAT_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

const MonitoringWorkspacePage = () => {
  const [cameras, setCameras] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [previewCamera, setPreviewCamera] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [pendingPreviewId, setPendingPreviewId] = useState(null);
  const [previewError, setPreviewError] = useState('');

  const apiBaseUrl = useMemo(() => config.api.baseURL.replace(/\/$/, ''), []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [cameraResponse, eventResponse] = await Promise.all([
        getCameraList(),
        getFallEvents({ page_size: 15 }),
      ]);
      setCameras(Array.isArray(cameraResponse) ? cameraResponse : []);
      setEvents(normalizeEvents(eventResponse));
    } catch (err) {
      console.error('Failed to load monitoring workspace', err);
      setError('Unable to fetch monitoring data right now. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenPreview = async (camera) => {
    if (!camera) return;
    setPreviewError('');
    setPendingPreviewId(camera.id);
    setPreviewLoading(true);
    try {
      await sendCameraHeartbeat(camera.id, { keepaliveSeconds: 120, source: 'live_preview' });
      setPreviewCamera(camera);
    } catch (err) {
      console.error('Failed to open live preview', err);
      setPreviewError('Unable to open live preview right now. Please try again.');
    } finally {
      setPreviewLoading(false);
      setPendingPreviewId(null);
    }
  };

  const handleClosePreview = () => {
    setPreviewCamera(null);
  };

  const classifyCamera = (camera) => {
    const lastPing = camera.last_health_check ? new Date(camera.last_health_check).getTime() : 0;
    const isStale = !lastPing || Date.now() - lastPing > STALE_HEARTBEAT_THRESHOLD_MS;
    const reachable = camera.is_active !== false;
    return {
      ...camera,
      lastPing,
      isStale,
      isReachable: reachable,
      isOnline: reachable && !isStale,
    };
  };

  const classifiedCameras = useMemo(() => cameras.map(classifyCamera), [cameras]);

  const cameraSummary = useMemo(() => {
    const total = classifiedCameras.length;
    const online = classifiedCameras.filter((camera) => camera.isOnline).length;
    const offline = total - online;
    const awaiting = classifiedCameras.filter((camera) => camera.isReachable && camera.isStale).length;
    return { total, online, offline, awaiting };
  }, [classifiedCameras]);

  const summaryLabel = useMemo(() => {
    const parts = [
      `${cameraSummary.online} online`,
      `${cameraSummary.offline} offline`,
      `${cameraSummary.awaiting} awaiting motion`,
    ];
    return parts.join(' \u2022 ');
  }, [cameraSummary.awaiting, cameraSummary.offline, cameraSummary.online]);

  const activeCameras = useMemo(
    () =>
      classifiedCameras
        .filter((camera) => camera.isOnline)
        .sort((a, b) => b.lastPing - a.lastPing)
        .slice(0, 4),
    [classifiedCameras],
  );

  const attentionCameras = useMemo(
    () =>
      classifiedCameras
        .filter((camera) => !camera.isOnline)
        .sort((a, b) => b.lastPing - a.lastPing)
        .slice(0, 6),
    [classifiedCameras],
  );

  const hasAttention = attentionCameras.length > 0;

  const alertQueue = useMemo(
    () =>
      (events || [])
        .filter((event) => event.status && !['CLOSED', 'ADMIN_REVIEWED'].includes(event.status))
        .sort((a, b) => new Date(b.timestamp || b.detected_at) - new Date(a.timestamp || a.detected_at)),
    [events],
  );

  const recentEvents = useMemo(() => events.slice(0, 4), [events]);

  return (
    <Container maxWidth="xl" sx={{ py: 5 }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Stack spacing={1}>
            <Typography variant="h4" fontWeight={700}>
              Monitoring workspace
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Observe live feeds, filter by zone, and triage alerts. This view brings together camera health and
              fall events so the team can act quickly.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={loadData} startIcon={<RefreshIcon />}>
              Refresh
            </Button>
            <Button variant="outlined" startIcon={<FilterListIcon />}>
              Filters
            </Button>
          </Stack>
        </Stack>

        {previewError && (
          <Alert severity="error" onClose={() => setPreviewError('')}>
            {previewError}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Card sx={{ p: 4, borderRadius: '12px !important' }}>
            <Typography color="error">{error}</Typography>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  p: 3,
                  borderRadius: '12px !important',
                  minHeight: activeCameras.length ? 360 : 220,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" color="text.secondary">
                    Camera overview
                  </Typography>
                  <Chip label={summaryLabel} size="small" sx={{ borderRadius: 1 }} />
                </Stack>

                <ActiveCameraGrid
                  cameras={activeCameras}
                  onWatchLive={handleOpenPreview}
                  loadingCameraId={previewLoading ? pendingPreviewId : null}
                />
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 3,
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: hasAttention ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)',
                  },
                  alignContent: 'start',
                }}
              >
                <Card
                  sx={{
                    p: 3,
                    borderRadius: '12px !important',
                    gridColumn: { lg: '1 / -1' },
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Status overview
                  </Typography>
                  <Stack spacing={2}>
                    <StatusRow
                      icon={<SensorsIcon color="primary" />}
                      label="Cameras online"
                      value={`${cameraSummary.online} of ${cameraSummary.total}`}
                    />
                    <StatusRow
                      icon={<WarningAmberIcon color="warning" />}
                      label="Alerts awaiting review"
                      value={alertQueue.length}
                    />
                    <StatusRow
                      icon={<WarningAmberIcon color="error" />}
                      label="Awaiting motion heartbeat"
                      value={cameraSummary.awaiting}
                    />
                    <StatusRow
                      icon={<VideocamIcon color="action" />}
                      label="Recent incidents"
                      value={recentEvents.length}
                    />
                  </Stack>
                </Card>

                {hasAttention && (
                  <Box sx={{ gridColumn: { xs: '1 / -1', lg: 'span 1' } }}>
                    <AttentionCameraList
                      cameras={attentionCameras}
                      onWatchLive={handleOpenPreview}
                      loadingCameraId={previewLoading ? pendingPreviewId : null}
                    />
                  </Box>
                )}

                <Card
                  sx={{
                    p: 3,
                    borderRadius: '12px !important',
                    gridColumn: { xs: '1 / -1', lg: hasAttention ? 'span 1' : '1 / -1' },
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" color="text.secondary">
                      Alert queue
                    </Typography>
                    <IconButton size="small" onClick={loadData}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  {alertQueue.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      All incidents are reviewed. Any new fall alerts will appear here automatically.
                    </Typography>
                  ) : (
                    <List
                      dense
                      disablePadding
                      sx={{
                        maxHeight: 264,
                        overflowY: 'auto',
                        pr: 1,
                      }}
                    >
                      {alertQueue.map((event, index) => {
                        const statusToken = resolveStatusToken(event.status, 'fallEvents');
                        return (
                          <React.Fragment key={event.id}>
                            <ListItem alignItems="flex-start" sx={{ px: 0, py: 1.5 }}>
                              <ListItemText
                                primary={(
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body1" fontWeight={600}>
                                      {event.camera?.name || event.camera_name || 'Unknown camera'}
                                    </Typography>
                                    <Chip
                                      label={statusToken?.label || event.status}
                                      size="small"
                                      color={statusToken?.color === 'default' ? 'primary' : statusToken?.color}
                                      sx={{ borderRadius: 1, textTransform: 'capitalize' }}
                                    />
                                  </Stack>
                                )}
                                secondary={(
                                  <Typography variant="body2" color="text.secondary">
                                    {event.timestamp
                                      ? new Date(event.timestamp).toLocaleString()
                                      : 'Unspecified time'}
                                  </Typography>
                                )}
                              />
                            </ListItem>
                            {index < alertQueue.length - 1 && <Divider component="li" />}
                          </React.Fragment>
                        );
                      })}
                    </List>
                  )}
                </Card>
              </Box>
            </Grid>
          </Grid>
        )}
      </Stack>

      <LivePreviewDialog
        open={Boolean(previewCamera)}
        camera={previewCamera}
        apiBase={apiBaseUrl}
        onClose={handleClosePreview}
      />
    </Container>
  );
};

const StatusRow = ({ icon, label, value }) => (
  <Stack direction="row" spacing={2} alignItems="center">
    <Box sx={{ display: 'flex' }}>{icon}</Box>
    <Stack>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  </Stack>
);

export default MonitoringWorkspacePage;
